import type { InvoiceSource, MatchState, SupplierInvoice, Voucher, VoucherStatus } from './types';

/** Số tiền triệu ₫ theo board: 1 số lẻ, dấu phẩy (`7,4`), âm dùng dấu − . */
export const f1 = (x: number): string => (x < 0 ? '−' : '') + Math.abs(x).toFixed(1).replace('.', ',');

export type StateTone = 'ok' | 'warn' | 'gray' | 'violet' | 'err';

/** Trạng thái ghép hoá đơn → tông chip (Mint-Cost `SC`). */
export const MATCH_TONE: Readonly<Record<MatchState, StateTone>> = {
  'Đã ghép': 'ok',
  'Một phần': 'warn',
  'Chưa ghép': 'gray',
  'Cần review': 'violet',
  'Huỷ': 'err',
  'Không gắn lô': 'gray',
};

export const SOURCE_LABEL: Readonly<Record<InvoiceSource, string>> = { XML: 'XML', VETC: 'VETC', Excel: 'Excel' };

/** Tìm không dấu theo số HĐ · đối tác · số hoá đơn · job · nhóm (Mint-Cost `norm`). */
export const norm = (x: string): string => x.normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/đ/g, 'd').toLowerCase();

export function searchInvoices(rows: readonly SupplierInvoice[], q: string): SupplierInvoice[] {
  const n = norm(q.trim());
  if (!n) return [...rows];
  return rows.filter((r) => norm(`${r.id} ${r.partnerName} ${r.partner} ${r.jobs} ${r.invNo} ${r.group}`).includes(n));
}

/** Tổng thanh: tổng tiền HĐ (tr ₫) và số HĐ chưa ghép đủ dòng. */
export function invoiceTotals(rows: readonly SupplierInvoice[]): { total: number; unmatched: number } {
  return { total: rows.reduce((a, r) => a + r.amount, 0), unmatched: rows.filter((r) => r.matched < r.lines).length };
}

// ---- Bảng phải · Phân tích (Mint-Cost `an*`): số tổng hợp tháng 10 lấy theo board, loại chi phí / đối tác tính từ hoá đơn ----

export type DockTone = 'blue' | 'amber' | 'violet' | 'green' | 'gray' | 'red';
export interface DockBar { k: string; v: string; w: string; tone: DockTone }

export const AN_KPI: readonly { k: string; v: string; u: string; tone?: 'warn' | 'err' }[] = [
  { k: 'Chi phí tháng', v: '1.284', u: 'tr ₫' },
  { k: 'Chờ HĐ', v: '186,4', u: 'tr ₫', tone: 'warn' },
  { k: 'Đề nghị chờ duyệt', v: '471,5', u: 'tr ₫' },
  { k: 'Tạm ứng quá hạn', v: '4,5', u: 'tr ₫', tone: 'err' },
];

const AGE: readonly [string, number, DockTone][] = [['0–7 ngày', 92.1, 'blue'], ['8–15 ngày', 54.8, 'amber'], ['16–30 ngày', 31.0, 'amber'], ['> 30 ngày', 8.5, 'red']];
export const AN_AGE: readonly DockBar[] = AGE.map(([k, v, tone]) => ({ k, v: f1(v), w: `${Math.round((v / 92.1) * 100)}%`, tone }));

/** Chi theo dịch vụ (donut r = 52, chu vi 2πr) — mỗi cung gap 3 khi dài hơn 6. */
const SVC: readonly [string, number, DockTone][] = [['Cước biển', 812, 'blue'], ['Local / cảng', 214, 'amber'], ['Air', 142, 'violet'], ['Trucking', 78, 'green'], ['Hải quan', 38, 'gray']];
export const AN_SVC_TOTAL = SVC.reduce((a, x) => a + x[1], 0);
export function svcDonut(): { k: string; v: string; p: string; tone: DockTone; dash: string; off: string }[] {
  const C = 2 * Math.PI * 52;
  let acc = 0;
  return SVC.map(([k, v, tone]) => {
    const len = (v / AN_SVC_TOTAL) * C;
    const gap = len > 6 ? 3 : 0;
    const d = { k, tone, v: v.toLocaleString('vi-VN'), p: `${Math.round((v / AN_SVC_TOTAL) * 100)}%`, dash: `${Math.max(len - gap, 0).toFixed(1)} ${(C - len + gap).toFixed(1)}`, off: (-acc).toFixed(1) };
    acc += len;
    return d;
  });
}

const REC: readonly [string, number][] = [['TANCANGSG', 92], ['SP-ITC', 78], ['VETC', 100], ['HDUONG', 85], ['ONE-LINE', 64]];
export const AN_REC: readonly DockBar[] = REC.map(([k, v]) => ({ k, v: `${v}%`, w: `${v}%`, tone: v >= 85 ? 'green' : v >= 70 ? 'amber' : 'red' }));

export const AN_MONTHS: readonly { k: string; v: number; h: number; now: boolean }[] = (
  [['05', 842], ['06', 905], ['07', 951], ['08', 1012], ['09', 968], ['10*', 694]] as const
).map(([k, v]) => ({ k, v, h: Math.round((v / 1012) * 62), now: k === '10*' }));

/** Top 5 nhóm chi phí theo tiền HĐ. */
export function topGroups(rows: readonly SupplierInvoice[]): { k: string; v: string; w: string }[] {
  const g = new Map<string, number>();
  for (const r of rows) g.set(r.group, (g.get(r.group) ?? 0) + r.amount);
  const a = [...g].sort((x, y) => y[1] - x[1]).slice(0, 5);
  const mx = a[0]?.[1] || 1;
  return a.map(([k, v]) => ({ k, v: f1(v), w: `${Math.round((v / mx) * 100)}%` }));
}

/** Top 5 đối tác theo tiền HĐ (kèm số HĐ). */
export function topPartners(rows: readonly SupplierInvoice[]): { k: string; n: number; sum: number; v: string; w: string }[] {
  const g = new Map<string, [number, number]>();
  for (const r of rows) {
    const e = g.get(r.partner) ?? [0, 0];
    g.set(r.partner, [e[0] + r.amount, e[1] + 1]);
  }
  const a = [...g].sort((x, y) => y[1][0] - x[1][0]).slice(0, 5);
  const mx = a[0]?.[1][0] || 1;
  return a.map(([k, [sum, n]]) => ({ k, n, sum, v: f1(sum), w: `${Math.round((sum / mx) * 100)}%` }));
}

/** Đề nghị theo bước duyệt (6 nhóm, bỏ nhóm 0). */
const STEP_GROUPS: readonly [string, readonly VoucherStatus[], DockTone][] = [
  ['Created · chờ cấp 1', ['c1'], 'amber'],
  ['Confirmed · chờ kế toán', ['kt'], 'blue'],
  ['Chờ ACC-CA', ['gd'], 'violet'],
  ['Approved · trong đợt chi', ['done', 'rq'], 'green'],
  ['Payment Done', ['paid'], 'gray'],
  ['Need Revise', ['back'], 'red'],
];
export function stepGroups(rows: readonly Pick<Voucher, 'status'>[]): { k: string; n: number; tone: DockTone }[] {
  return STEP_GROUPS.map(([k, ks, tone]) => ({ k, tone, n: rows.filter((r) => ks.includes(r.status)).length })).filter((x) => x.n > 0);
}

export const AN_TASKS: readonly { code: string; k: string; due: string; urgency: 0 | 1 | 2 }[] = [
  { code: 'EXI', k: 'HĐ TANCANGSG chưa về · 5 dòng quá 7 ngày', due: 'Hôm nay 17:30', urgency: 1 },
  { code: 'ADV', k: 'KIỆT quá hạn quyết toán 3,0 tr', due: 'Quá hạn 4 ngày', urgency: 2 },
  { code: 'GPW', k: 'SE260617270 GP âm · rà chi phí cắm điện', due: 'Hôm nay', urgency: 1 },
  { code: 'RV', k: 'Review HĐ thay thế PI26097290 (ONE-LINE)', due: 'Ngày mai 10:00', urgency: 0 },
  { code: 'HT', k: 'Hạch toán 3 dòng lệch bảng kê TANCANGSG', due: 'Ngày mai', urgency: 0 },
];
